import { AnnotatedBoundingBox } from "extractedBackendTypes";
import { MouseEvent } from "react";
import {
  PAGE_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  LABELS_PARAMETER,
  FOLDER_PARAMETER,
  VERTEX_ID,
  VERTEX_TYPE,
  ISSUE_TYPE_PARAMETER,
  GRANULARITY_PARAMETER,
  USER_TAGS_PARAMETER,
  ISSUE_CONFIDENCE,
  DUPLICATE_THRESHOLD,
  METADATA_SOURCES_PARAMETER,
  UNIQUENESS_SCORE_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  SEARCH_TEXT_THRESHOLD_PARAMETER,
  VERTEX_CONTEXT,
  OBJECT_ID_QUERY_PARAMETER,
  VQL_PARAMETER,
  FLAT_RESPONSE_PARAMETER,
  VERTEX_SIMILARITY,
} from "helpers/constants/miscellaneous";

export type ItemCenter = {
  x: number;
  y: number;
};

export type User = {
  origin?: string;
  session?: string;
};

export enum TabName {
  Explorer = "Datasets",
  Debug = "Debug",
}

export type Tab = {
  name: TabName;
  path: string;
};

export enum Status {
  Init = "Init",
  Loading = "Loading",
  Loaded = "Loaded",
}

export type TreeNode = {
  components: Array<TreeNode>;
  name?: string;
  component_id: string;
  num_images: number;
  preview?: string;
  previews?: Array<string>;
  src?: string;
  type: string;
};

export type Tree = {
  status: Status;
  root?: TreeNode;
  breadcrumbs: Array<string>;
};

export enum DatasetSourceEnum {
  UPLOAD = "UPLOAD",
  PUBLIC_BUCKET = "PUBLIC_BUCKET",
  BUCKET = "BUCKET",
  VL = "VL",
}

export interface Dataset {
  created_at: string;
  created_by: string;
  deleted: boolean;
  description: string;
  display_name: string;
  enrichment_models: { enrichment_models: any };
  fatal_error_msg: string | null;
  filename: any;
  hidden: boolean;
  id: string;
  images_duplicate_thresholds: number[] | null;
  n_clusters: number;
  n_images: number;
  n_objects: number;
  n_video_frames: number;
  n_videos: number;
  objects_duplicate_thresholds: number[] | null;
  owned_by: string;
  pipeline_commit_id: string;
  preview_uri: string;
  progress: number;
  sample: "DEFAULT_SAMPLE" | null;
  score: number;
  similarity_data: boolean;
  size_bytes: number;
  size_display_value: string | null;
  source_dataset_id: string;
  source_type: DatasetSourceEnum;
  source_uri: string;
  stats: any;
  status: DatasetStatusEnum;
  status_new: EDatasetStatus;
  metadata: unknown & { camtek_db_export: Record<string, string | string[]> };
}

export type ContentLoadProps = {
  radius?: string;
};

export type DropDownItem = {
  id: number;
  text: string;
};

export type FilterOption = MetadataItem & {
  selected?: boolean;
  source?: string;
};

export type MetadataItem = {
  id: number;
  text: string;
};

export type FilteredCounts = {
  total: number;
  filtered: number;
};

export type GlobalCounts = {
  clusters: FilteredCounts;
  images: FilteredCounts;
  objects: FilteredCounts;
};
// Feature Config types - matches backend clustplorer/clustplorer_models/feature_types.py
export type FeatureUIBehavior =
  | "HIDE"
  | "SHOW"
  | "GREY_OUT"
  | "TOGGLE"
  | "MULTI_SELECT";

export type UIContext = "SIMILARITY_SEARCH" | "ANY";

/**
 * Raw feature config as returned by the backend API /api/v1/user_config endpoint.
 * Maps to Python FeatureConfig in clustplorer/clustplorer_models/feature_types.py
 */
export interface RawFeatureConfig {
  feature_key: string;
  feature_behavior: FeatureUIBehavior;
  contexts: UIContext[];
  feature_options?: Record<string, unknown>;
  reason?: string;
}

/**
 * Response from GET /api/v1/user_config endpoint
 */
export interface UserConfigApiResponse {
  features: RawFeatureConfig[];
  workspaces?: Array<{
    id: string;
    organization_id: string;
    name: string;
    permissions?: {
      can_view: boolean;
      can_manage_members: boolean;
      can_edit_roles: boolean;
      can_create_dataset: boolean;
    };
  }>;
}

/**
 * Transformed feature config used throughout the frontend.
 * Created by transformFilterData() from RawFeatureConfig[].
 * Groups feature behaviors by context for easier lookup.
 */
export interface TransformedFeatureConfig {
  feature_key: string;
  feature_behavior: Partial<Record<UIContext, FeatureUIBehavior | null>>;
  featureOptions?: Record<string, unknown>;
  reason?: string;
}

export type MetadataBarTypes =
  | "labels"
  | "file-display"
  | "user-tags"
  | "issue-types"
  | "models"
  | "sources";

export type MetadataSectionTypes =
  | "vl-enrichment"
  | "metadata"
  | "user-tags"
  | "quality"
  | "sources"
  | "labels"
  | null;

export type FilterItemCounts = {
  title: string;
  count: number;
};

export type FilterItemCountsWithSelect = {
  id?: string;
  title: string;
  count: number;
  selected: boolean;
  ratio?: number;
};

export type MetadataSource = FilterItemCountsWithSelect & {
  iconPath?: string;
  type?: ModelTypes;
  isSelectable?: boolean;
};

export type RawMetadataSummaryData = {
  showVLEnrichment: boolean;
  videoData: Array<FilterItemCounts>;
  userTags: Array<FilterItemCounts>;
  totalElements: number;
};

export type MetadataSummaryData = {
  showVLEnrichment: boolean;
  videoData: Array<FilterItemCountsWithSelect>;
  userTags: Array<FilterItemCountsWithSelect>;
  issueTypes: Array<FilterItemCountsWithSelect>;
  mediaLabels: Array<FilterItemCountsWithSelect>;
};

export type EventTarget = "Image" | "Object" | "Cluster" | "Video";

export type ContactUsComponentType =
  | "textual_search"
  | "vl_enrichment"
  | "create_newdataset_private_s3_bucket"
  | "create_newdataset_dropbox"
  | "fastdup_exploration"
  | "upgrade_plan"
  | "free_trial"
  | "";

export type FormDataKeys = {
  fullName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  company: string;
  notes?: string;
  datasetID?: string;
  calledFrom: ContactUsComponentType;
};

export type SetStateFunction<T> = React.Dispatch<React.SetStateAction<T>>;

export type UserInfo = {
  email: string;
  isInternalUser: boolean;

  name?: string;
  userId?: string;
  userAvatar?: string;
  identityProvider?: string;
  username?: string | null;
};

export type singleImageObjectType = {
  boundingBox: number[];
  color: string;
  id: string;
  name: string;
  selected: boolean;
  source: string;
};

export type HoveredAssetDataType = {
  id: string;
  dimensions: NaturalDimensions;
  error: boolean;
};
export type OnUpdateAssetData = (id: string, source: string) => void;

export type Label = {
  categoryName: string;
  labels: any[];
  color: string;
  type: string;
  source: string;
};

export type TreeData = {
  key: string;
  title: string;
  children?: TreeData[];
  isLeaf?: boolean;
};

export type NaturalDimensions = { naturalWidth: number; naturalHeight: number };

// Upload - for user uploaded image search or a region of interest
// Cluster - for find similar on a cluster
// Media - for find similar images or objects
export type VertexType = "UPLOAD" | "CLUSTER" | "MEDIA";

export enum StepTileProgressStatus {
  ACTIVE = "ACTIVE",
  COMPLETE = "COMPLETE",
  DISABLED = "DISABLED",
  FAILED = "FAILED",
  IN_PROGRESS = "IN_PROGRESS",
}

export enum DataSources {
  FOLDER = "FOLDER",
  ZIP = "ZIP",
  S3 = "S3",
  KAGGLE = "KAGGLE",
  G_DRIVE = "G_DRIVE",
  PVT_S3_BUCKET = "PVT_S3_BUCKET",
  DROPBOX = "DROPBOX",
}

export enum AnnotationSources {
  UPLOAD = "UPLOAD",
  CVAT = "CVAT",
  LABELBOX = "LABELBOX",
}

export interface DataPreview {
  file_name?: string;
  thumbnail: string;
  object_annotations?: AnnotatedBoundingBox[];
  image_annotations?: string[];
}

export enum TabTypes {
  DATA_EXPLORATION = "DATA_EXPLORATION",
  DATA_INGESTION = "DATA_INGESTION",
}

export enum DatasetStatusEnum {
  UPLOADING = "UPLOADING",
  INDEXING = "INDEXING",
  FATAL_ERROR = "FATAL_ERROR",
  INITIALIZING = "INITIALIZING",
  READY = "READY",
  ENRICHING = "ENRICHING",
  NEW = "NEW",
  PRE_PROCESSING = "PRE_PROCESSING",
  SAVING = "SAVING",
  UPDATING = "UPDATING",
  PENDING_INDEX = "PENDING_INDEX",
}

export interface SecondaryStatusItem {
  status: string;
  progress: number;
}

export enum EDatasetStatus {
  DRAFT = "DRAFT",
  INDEXING = "INDEXING",
  READY = "READY",
  READ_ONLY = "READ ONLY",
  PARTIAL_INDEX = "PARTIAL INDEX",
  ERROR = "ERROR",
}

export interface CreationStatusResponse {
  status: DatasetStatusEnum;
  status_new: EDatasetStatus;
  fatalError: string | null;
  secondaryStatus: SecondaryStatusItem[];
  taskType: string;
}

export type LastUsedData = {
  s3Url: string;
  folderName: string;
  annotationFiles: string[];
};

export enum DatasetTypeEnum {
  All = "All",
  Shared = "Shared with me",
  Owned = "Owned by me",
}

export enum FileType {
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  UNKNOWN = "UNKNOWN",
}

export type FilesUploadSize = {
  bytes: number;
  length: number;
};

export type ActivityLogShowModalType = {
  show: boolean;
  title: string;
  reason: string;
  severity: "ERROR" | "WARNING" | "";
};

export type CopyURLTypes = "/create" | "/data" | null;

export type UserQuota = {
  accountType: string;
  totalQuota: number;
  usedQuota: number;
  availableQuota: number;
  email: string | null;
};

export enum DatasetSortEnum {
  DATE = "date",
  SIZE = "size",
  NAME = "name",
  STATUS = "status",
}

export type DatasetsFilterValue = { id: string; selected: boolean };

export interface InventoryFilterData {
  searchValue: null | string;
  datasetType: DatasetTypeEnum;
  datasetStatus: DatasetsFilterValue[];
  deprecatedDatasetStatus: DatasetsFilterValue[];
  datasetJob: DatasetsFilterValue[];
  datasetSetup: DatasetsFilterValue[];
  datasetRecipe: DatasetsFilterValue[];
  sortValue: DatasetSortEnum;
}

export type DatasetFilterIconType =
  | "Ready"
  | "Resume"
  | "Failed"
  | "Indexing"
  | "Uploading"
  | "Private"
  | "Public"
  | "Date "
  | "Size";

export type HandleSelect = (
  e: MouseEvent,
  selected: boolean,
  specificIndex: string,
  clusterID: string,
  singleData: any,
) => void;

export interface RawCVATProject {
  id: number;
  name: string;
}

export interface RawCVATTask extends RawCVATProject {
  project_id: number;
}

export interface CVATTask extends RawCVATProject {}

export interface CVATProject extends RawCVATProject {
  tasks: CVATTask[];
}

export enum CVATTypes {
  PROJECT = "PROJECT",
  TASK = "TASK",
}

export enum ModelTypes {
  IMAGE_TAGGING = "IMAGE_TAGGING",
  OBJECT_TAGGING = "OBJECT_TAGGING",
  OBJECT_DETECTION = "OBJECT_DETECTION",
  MULTIMODEL_IMAGE_ENCODING = "MULTIMODEL_IMAGE_ENCODING",
  MULTIMODEL_OBJECT_ENCODING = "MULTIMODEL_OBJECT_ENCODING",
  INSTANCE_RETRIEVAL = "INSTANCE_RETRIEVAL",
}

export enum EnrichmentStatus {
  NEW = "NEW",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  ABORTED = "ABORTED",
  FAILED = "FAILED",
}

export interface PreviewModelActionStatus {
  value: boolean;
  reason: string;
}

export interface EnrichmentModel {
  id: string;
  name: string;
  type: ModelTypes;
  description: string;
  release_date: string | null;
  vendor_logo_uri: string;
  status: "Active" | "Coming Soon";
  documentation_uri: string;
  metadata: { [key: string]: string };
  selectable: PreviewModelActionStatus;
  replaced?: boolean;
  previews?: boolean;
  vqa_prompt?: boolean;
  dependencies?: string[] | null;
}

export interface ModelSelectionData {
  [datasetId: string]: {
    models: EnrichmentModel[];
    selectedModel: EnrichmentModel | null;
  };
}

export enum EnrichmentModelSelectedStatus {
  MODEL_SELECTED = "MODEL_SELECTED",
  MODEL_PREVIOUSLY_SELECTED = "MODEL_PREVIOUSLY_SELECTED",
  MODEL_NOT_SELECTED = "MODEL_NOT_SELECTED",
}

export interface EnrichmentModelOption extends EnrichmentModel {
  selectedStatus: EnrichmentModelSelectedStatus;
}

export interface EnrichmentModelForPreview extends EnrichmentModel {
  deletable: PreviewModelActionStatus;
  editable: PreviewModelActionStatus;
}

export enum ModelVarient {
  ENRICHMENT = "ENRICHMENT",
  CREATION = "CREATION",
}

export enum MetadataType {
  USER_TAGS = "USER_TAGS",
  IMAGE_LABELS = "LABELS",
  OBJECT_LABELS = "OBJECTS",
}

interface EnrichmentPreviewBase {
  media_id: string;
  media_uri: string;
  media_thumb_uri: string;
  image_id: string | null;
  image_uri: string | null;
  file_name: string;
  original_uri: string | null;
  video_uri: string | null;
  frame_timestamp: number | null;
  labels: string[] | null;
  user_tags: string[] | null;
  relevance_score: number | null;
  relevance_score_type: string | null;
  is_pending: boolean;
}

export interface EnrichmentPreview extends EnrichmentPreviewBase {
  type: "IMAGE" | "OBJECT";
  bounding_box: number[] | null;
  objects: EnrichmentPreviewObject[];
}

export interface EnrichmentPreviewObject extends EnrichmentPreviewBase {
  type: "OBJECT";
  bounding_box: number[];
}

export interface SelectionAlertModel extends EnrichmentModelOption {
  selectionType: "alert" | "override";
}

export type GlobalMDType = {
  type: "IMAGE" | "OBJECT";
  metadataType: MetadataType;
};

export enum CardTypes {
  IMAGES = "IMAGES",
  OBJECTS = "OBJECTS",
  IMAGE = "IMAGE",
  OBJECT = "OBJECT",
}

export interface EnrichingDS {
  id: string;
  name: string;
  progress: number;
  aborted?: boolean;
  type: "IN_PROGRESS" | "SUCCESS";
}

export type MDOption = {
  type: MetadataType;
  icon: JSX.Element;
  title: string;
};

export interface UserApiResponse {
  user_id?: string;
  email: string;
  name: string;
  avatar_uri?: string;
  is_internal_user?: string;
}

export interface MemberWithRole {
  user: UserApiResponse;
  role: string;
}

export type SavedView = {
  id: string;
  name: string;
  filters: SavedFilters;
  createdAt: string;
  createdBy: UserApiResponse;
  stats: {
    n_images: number;
    n_objects: number;
    n_video_frames: number;
  };
};

export interface SavedFilters extends Omit<APIQueryParams, "vql"> {
  cluster_id?: string;
  vql?: VqlQuery[];
}

export interface DuplicatesThresholdRange {
  imagesDuplicateThresholds: number[];
  objectsDuplicateThresholds: number[];
}

export enum IssueTypes {
  MISLABELS = "mislabels",
  DUPLICATES = "duplicates",
}

export interface UniquenessData {
  imageId: string;
  objectId: string | null;
  uniquenessScore: number;
}

export type TrackingFilterTypes =
  | "Labels"
  | "Files"
  | "Issue Types"
  | "User Tags"
  | "Enrichment Model"
  | "Metadata Source";

export enum CardEntityTypes {
  Frame = "Frame",
  Image = "Image",
  Object = "Object",
  Frames = "Frames",
  Images = "Images",
  Objects = "Objects",
}

export type CardPreviewData = {
  media_id: string;
  image_id: string;
  media_uri: string;
  image_uri: string;
  file_name: string;
  original_uri: string;
  labels: string[] | null;
  media_thumb_uri: string;
  video_uri: string | null;
  type: "IMAGE" | "OBJECT";
  user_tags: string[] | null;
  bounding_box: number[] | null;
  frame_timestamp: string | null;
  relevance_score: number | null;
  relevance_score_type: string | null;
};

export type EntityType = "IMAGES" | "OBJECTS";

export const isValidEntityType = (
  value: string | undefined | null,
): value is EntityType => {
  const validEntityTypes: EntityType[] = ["IMAGES", "OBJECTS"];
  return validEntityTypes.includes(value as EntityType);
};

export interface CardData {
  cluster_id: string;
  n_images: number;
  n_videos: number;
  n_frames: number;
  n_objects: number;
  formed_by: string;
  type: EntityType;
  size_display_value: string;
  previews: CardPreviewData[];
  similarity_threshold: string;
  relevance_score: number | null;
  labels: { [key: string]: number };
  user_tags: { [key: string]: any };
  relevance_score_type: string | null;
}

export type CardBodyImage = {
  id: string;
  src: string;
  relevanceType: RelevanceType | null;
  timestamp?: number;
};

export enum CardVariant {
  DEFAULT = "DEFAULT",
  SELECTED = "SELECTED",
  VERTEX = "VERTEX",
}

export interface APIQueryParams {
  threshold?: string;
  duplicate_threshold?: string;
  labels?: string;
  issues?: string;
  image_filename?: string;
  caption?: string;
  textual_similarity_threshold?: string;
  path?: string;
  anchor_type?: string;
  anchor_context?: string;
  entity_type?: string;
  page_number?: string;
  tags?: string;
  anchor_media_id?: string;
  issue_confidence?: string;
  metadata_sources?: string;
  uniqueness_score?: string;
  vql?: string;
  flat_response?: string;
  vs?: string;
}

export type QueryParameters =
  | typeof PAGE_PARAMETER
  | typeof VERTEX_CONTEXT
  | typeof VERTEX_ID
  | typeof VERTEX_TYPE
  | typeof ENTITY_TYPE_PARAMETER
  | typeof SEARCH_TEXT_PARAMETER
  | typeof LABELS_PARAMETER
  | typeof FOLDER_PARAMETER
  | typeof ISSUE_TYPE_PARAMETER
  | typeof USER_TAGS_PARAMETER
  | typeof GRANULARITY_PARAMETER
  | typeof ISSUE_CONFIDENCE
  | typeof DUPLICATE_THRESHOLD
  | typeof UNIQUENESS_SCORE_PARAMETER
  | typeof METADATA_SOURCES_PARAMETER
  | typeof SEARCH_TEXT_THRESHOLD_PARAMETER
  | typeof VQL_PARAMETER
  | typeof OBJECT_ID_QUERY_PARAMETER
  | typeof FLAT_RESPONSE_PARAMETER
  | typeof VERTEX_SIMILARITY;

export function isQueryParameter(value: any): value is QueryParameters {
  const validParameters: QueryParameters[] = [
    PAGE_PARAMETER,
    VERTEX_CONTEXT,
    VERTEX_ID,
    VERTEX_TYPE,
    ENTITY_TYPE_PARAMETER,
    SEARCH_TEXT_PARAMETER,
    LABELS_PARAMETER,
    FOLDER_PARAMETER,
    ISSUE_TYPE_PARAMETER,
    USER_TAGS_PARAMETER,
    GRANULARITY_PARAMETER,
    ISSUE_CONFIDENCE,
    DUPLICATE_THRESHOLD,
    UNIQUENESS_SCORE_PARAMETER,
    METADATA_SOURCES_PARAMETER,
    SEARCH_TEXT_THRESHOLD_PARAMETER,
    VQL_PARAMETER,
    FLAT_RESPONSE_PARAMETER,
    VERTEX_SIMILARITY,
  ];

  return validParameters.includes(value);
}

export type FiltersObject = {
  [key in QueryParameters]?: string;
};

export type SearchFilterOptions = {
  semanticSearch: boolean;
  labelsSearch: boolean;
  enabled: boolean;
};

export type RelevanceType = "High" | "Medium" | "Low";

export type RelevanceItems = {
  id: number;
  text: RelevanceType;
  score: number;
};

export enum QueryOperator {
  IS = "IS",
  IS_NOT = "IS_NOT",
  IS_ONE_OF = "IS_ONE_OF",
  IS_NOT_ONE_OF = "IS_NOT_ONE_OF",
  ENDS_WITH = "ENDS_WITH",
  STARTS_WITH = "STARTS_WITH",
  CONTAINS = "CONTAINS",
  DOES_NOT_CONTAIN = "DOES_NOT_CONTAIN",
  BETWEEN = "BETWEEN",
  LESS_THAN = "LESS_THAN",
  GREATER_THAN = "GREATER_THAN",
  LESS_THAN_OR_EQUAL_TO = "LESS_THAN_OR_EQUAL_TO",
  GREATER_THAN_OR_EQUAL_TO = "GREATER_THAN_OR_EQUAL_TO",
  EQUAL = "EQUAL",
}

export const EXACT_MATCH_QUERY_OPERATORS = [
  QueryOperator.IS,
  QueryOperator.IS_NOT,
] as const;

export type ExactMatchQueryOperators =
  (typeof EXACT_MATCH_QUERY_OPERATORS)[number];

export const MATCH_QUERY_OPERATORS = [
  ...EXACT_MATCH_QUERY_OPERATORS,
  QueryOperator.IS_ONE_OF,
  QueryOperator.IS_NOT_ONE_OF,
] as const;

export type MatchQueryOperators = (typeof MATCH_QUERY_OPERATORS)[number];

export type SizeSupportedOperators =
  | QueryOperator.GREATER_THAN
  | QueryOperator.LESS_THAN
  | QueryOperator.GREATER_THAN_OR_EQUAL_TO
  | QueryOperator.LESS_THAN_OR_EQUAL_TO;

export const FILE_QUERY_OPERATORS = [
  QueryOperator.IS_ONE_OF,
  QueryOperator.STARTS_WITH,
  QueryOperator.ENDS_WITH,
  QueryOperator.CONTAINS,
] as const;

export type FileQueryOperators = (typeof FILE_QUERY_OPERATORS)[number];

export const DATE_TIME_OPERATORS = [
  QueryOperator.BETWEEN,
  QueryOperator.GREATER_THAN,
  QueryOperator.LESS_THAN,
  QueryOperator.GREATER_THAN_OR_EQUAL_TO,
  QueryOperator.LESS_THAN_OR_EQUAL_TO,
] as const;

export type CustomMetadataValue =
  | string
  | number
  | string[]
  | [string, string]
  | [number, number]
  | Record<string, any>;

export enum Filters {
  FILE = "FILE",
  LABELS = "LABELS",
  ISSUES = "ISSUES",
  FOLDER = "FOLDER",
  OUTLIERS = "OUTLIERS",
  LABEL_OUTLIER = "LABEL_OUTLIER",
  MISLABELS = "MISLABELS",
  USER_TAGS = "USER_TAGS",
  DUPLICATES = "DUPLICATES",
  UNIQUENESS = "UNIQUENESS",
  CREATED_AT = "CREATED_AT",
  MEDIA_STATUS = "MEDIA_STATUS",
}

export const RANGE_FILTERS = [
  Filters.ISSUES,
  Filters.OUTLIERS,
  Filters.MISLABELS,
  Filters.DUPLICATES,
  Filters.UNIQUENESS,
  Filters.LABEL_OUTLIER,
];

export enum FilterSubType {
  DARK = "DARK",
  BLUR = "BLUR",
  BRIGHT = "BRIGHT",
}

export const ISSUES_SUB_TYPES = [
  FilterSubType.DARK,
  FilterSubType.BLUR,
  FilterSubType.BRIGHT,
] as const;

export type FilterMenuItemType = Filters;

export type FilterMenuItem = {
  icon: JSX.Element;
  type: FilterMenuItemType;
  disabled?: boolean;
  entityTypes: EntityType[];
  hidden?: boolean;
};

export type QueryModalFilterOption = {
  id: number;
  text: string;
  count?: string;
};

export enum QueryPanelLogic {
  EXCLUDE = "EXCLUDE",
  AND = "AND",
  OR = "OR",
}

export type Query = {
  id: string;
  operator: QueryOperator;
  subType?: FilterSubType;
  logic: QueryPanelLogic;
  values: string[];
  type: FilterMenuItemType;
};

export interface VqlQueryBase {
  id: string;
}

export type VqlIssuesValues =
  | "mislabels"
  | "outliers"
  | "duplicates"
  | "blur"
  | "dark"
  | "bright"
  | "label_outlier"
  | "normal";

export type APIQueryOperators =
  | "one_of"
  | "not_one_of"
  | "is"
  | "is_not"
  | "contains"
  | "starts_with"
  | "ends_with"
  | "fts"
  | "semantic"
  | "media"
  | "upload"
  | "issues"
  | "duplicates"
  | "uniqueness"
  | "in"
  | "out"
  | "eq"
  | "neq"
  | "gt"
  | "lt"
  | "gte"
  | "lte"
  | "between"
  | "does_not_contain";

export type VqlMatchOps = Extract<
  APIQueryOperators,
  "is" | "is_not" | "one_of" | "not_one_of"
>;
export type VqlExactMatchOps = Extract<APIQueryOperators, "is" | "is_not">;
export type VqlStrOps = Extract<
  APIQueryOperators,
  "contains" | "starts_with" | "ends_with"
>;
export type VqlTextOps = Extract<APIQueryOperators, "fts" | "semantic">;
export type VqlSimilarityOps = Extract<APIQueryOperators, "media" | "upload">;
export type VqlModeOps = Extract<APIQueryOperators, "in" | "out">;

export type SizeFilterOp = Extract<APIQueryOperators, "media" | "upload">;
export type DateTimeFilterOp = Extract<
  APIQueryOperators,
  "between" | "gt" | "lt" | "gte" | "lte"
>;

// All possible filter types
export type LabelsFilter = {
  labels: { op: VqlMatchOps; value: string[] };
};
type IssueFilter = {
  issues: {
    op: "issue";
    value: VqlIssuesValues;
    confidence_min: number;
    confidence_max: number;
    mode: VqlModeOps;
  };
};
type DuplicatesFilter = {
  duplicates: {
    op: "duplicates";
    value: number;
  };
};
type UniquenessFilter = { uniqueness: { op: "uniqueness"; value: number } };

export type TagsFilter = { tags: { op: VqlMatchOps; value: string[] } };
type TextFilter = {
  text: {
    op: VqlTextOps;
    value: string;
    threshold?: number;
    negation?: boolean;
  };
};
type SimilarityFilter = {
  similarity: {
    op: VqlSimilarityOps;
    value: string;
    threshold?: number;
    bounding_box?: [number, number, number, number];
    negation?: boolean;
  };
};
type EntityTypeFilter = {
  entity_type: { op: VqlExactMatchOps; value: EntityType };
};
type FileFilter = {
  file: { op: VqlMatchOps | VqlStrOps; value: string | string[] };
};
type FolderFilter = {
  folder: { op: VqlMatchOps | VqlStrOps; value: string | string[] };
};

type SizeFilter = {
  size: { op: SizeFilterOp; value: number };
};

type DateTimeFilter = {
  id: string;
  created_at: {
    op: DateTimeFilterOp;
    value: string | [string, string];
  };
};

type MediaStatusFilter = {
  media_status: { op: VqlMatchOps; value: string };
};

type SemanticFilter = {
  semantic: { op: VqlTextOps; value: string; threshold?: number };
};

// TODO: record
// Union of all possible filter types
export type FilterType =
  | LabelsFilter
  | IssueFilter
  | DuplicatesFilter
  | TagsFilter
  | TextFilter
  | SimilarityFilter
  | EntityTypeFilter
  | FileFilter
  | FolderFilter
  | UniquenessFilter
  | SizeFilter
  | DateTimeFilter
  | MediaStatusFilter
  | SemanticFilter;

export type FilterKey = keyof (LabelsFilter &
  IssueFilter &
  DuplicatesFilter &
  TagsFilter &
  TextFilter &
  SimilarityFilter &
  EntityTypeFilter &
  FileFilter &
  FolderFilter &
  UniquenessFilter &
  SizeFilter &
  DateTimeFilter &
  MediaStatusFilter &
  SemanticFilter);

// Final type: base + exactly one filter
export type VqlQuery = VqlQueryBase & FilterType;

export function isFilterSubType(value: unknown): value is FilterSubType {
  return (
    typeof value === "string" &&
    Object.values(FilterSubType).includes(value as FilterSubType)
  );
}

export type Position = { top: number; left: number };

export type QueryModalData = {
  id?: string;
  type: FilterMenuItemType;
  position?: Position;
  fromSearch?: boolean;
};

export type RightPaneMetrics = "COUNT" | "PERCENTAGE";

export enum ExportType {
  CLUSTER = "cluster",
  MEDIA = "media",
}

export type BaseExportItem = {
  exportType: ExportType;
  cluster_id: string;
  media_id?: string;
  cluster_filters?: Record<string, any>;
  excludeMediaIDs?: string[];
  n_images: number;
};

export type MediaExportItem = BaseExportItem & {
  exportType: ExportType.MEDIA;
  media_id: string;
  image_uri: string;
};

export type ClusterExportItem = BaseExportItem & {
  exportType: ExportType.CLUSTER;
  cluster_id: string;
  previews: CardPreviewData[];
};

export type SingleExportItem = MediaExportItem | ClusterExportItem;

export type DatasetExportData = {
  exportDataArray: SingleExportItem[];
  tags: FilterOption[];
};

export type ExportData = { [DatasetId: string]: DatasetExportData };

// Custom Metadata Filter Types
export type CustomMetadataFieldType =
  | "string"
  | "float"
  | "datetime"
  | "enum"
  | "geolocation"
  | "link";

export type ImageCustomMetadata = {
  field_type: CustomMetadataFieldType;
  value: CustomMetadataValue;
  field_name: string;
};

export interface UseQueryModalReturn {
  isSubmitDisabled: boolean;
}

//New Dataset Creation Workflow Types
export enum FolderUploadStatus {
  IDLE = "IDLE",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export type UploadingFolder = {
  name: string;
  progress: number;
  transactionId: string;
};

export type MultiFolderUploadState = {
  folders: UploadingFolder[];
  overallProgress: number;
  status: FolderUploadStatus;
};

// Backend folder statuses
export type BackendFolderStatus =
  | "uploading"
  | "validating"
  | "valid"
  | "invalid"
  | "ready";

// Frontend folder statuses
export type FolderStatus = "uploading" | "validating" | "valid" | "invalid";

export type FolderMetadata = {
  id: string;
  status: FolderStatus;
  backend_status: BackendFolderStatus; // Store original backend status
  selected: boolean;
  folder_name: string;
  created_at: string;
  updated_at: string;
  preview_urls: string[];

  invalid_reason: string | null;
  metadata: Record<string, string | string[]> | null;
  total_images: number;
  upload_percentage: number;
  batch_id?: string;
  hidden?: boolean;
};

// Dataset Creation Preview Types
// Metadata field keys enum for dataset folder validation
export enum MetadataFieldKeys {
  JOB_NAME = "Job Name",
  SETUP_NAME = "Setup Name",
  RECIPES_NAME = "Recipes Name",
}

export type PreviewItem = {
  dataset_id: string;
  s3_url: string;
  file_name: string;
  scale_factor: number;
  thumbnail: string;
  image_annotations: string[] | null;
  object_annotations: AnnotatedBoundingBox[] | null;
};

export type SetFoldersResponse = {
  invalid_folders: Record<string, string>;
  processed_folders: string[];
};

export enum ExportModalSource {
  CART = "CART",
  CONTAINER_HEADER = "CONTAINER_HEADER",
}
